/** The longest side an edited/collage export is allowed to reach. A full-natural-size annotate/collage canvas (screenshots
 *  combined side-by-side) can exceed both the upload byte cap AND the server's 100 MP `sharp` decode limit; bounding the
 *  longest side keeps the PNG small while staying high-resolution (4096² = ~16 MP, well under 100 MP) and text/edges crisp. */
const MAX_EXPORT_DIM = 4096;

/**
 * Return a canvas whose longest side is ≤ {@link MAX_EXPORT_DIM}, downscaling (aspect-preserving, high-quality) only when the
 * source exceeds it — otherwise the SAME canvas is returned untouched. Used by both the annotate and collage editors right
 * before `toBlob`, so the exported image can't blow the step-image-edit upload cap or the server-side decode-pixel limit.
 */
export function boundedForExport(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const longest = Math.max(canvas.width, canvas.height);
  if (longest <= MAX_EXPORT_DIM) return canvas;
  const scale = MAX_EXPORT_DIM / longest;
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(canvas.width * scale));
  out.height = Math.max(1, Math.round(canvas.height * scale));
  const ctx = out.getContext("2d");
  if (!ctx) return canvas; // no 2D context (shouldn't happen) → export at full size rather than lose the edit
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}
